import React, { useMemo, useState, useEffect, useCallback } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {MapContainer, Marker, TileLayer, Tooltip, Circle, useMapEvents} from "react-leaflet";
import MarkerClusterGroup from 'react-leaflet-cluster';
import {Box, Fade, Paper, Typography, IconButton} from '@mui/material';
import {Close as CloseIcon, Search as SearchAreaIcon, Info as InfoIcon} from '@mui/icons-material';
// MapSearchControl moved to ControlPanel

// Memoized emoji icon creation for better performance
const iconCache = new Map();

// Component to track map center and show circular search area
const SearchAreaOverlay = ({ showOverlay, onToggleOverlay, onRadiusUpdate }) => {
    const [center, setCenter] = useState(null);
    const [radiusKm, setRadiusKm] = useState(25);
    const maxRadiusKm = 25; // Reduced from 50km to be more reasonable
    
    const map = useMapEvents({
        moveend: () => {
            updateCenterAndRadius();
        },
        zoomend: () => {
            updateCenterAndRadius();
        }
    });

    const updateCenterAndRadius = useCallback(() => {
        if (!map) return;
        
        const mapCenter = map.getCenter();
        const bounds = map.getBounds();
        const corner = bounds.getNorthEast();
        
        // Calculate radius from center to corner (same logic as in App.js)
        const calculatedRadiusKm = map.distance([mapCenter.lat, mapCenter.lng], [corner.lat, corner.lng]) / 1000;
        const cappedRadiusKm = Math.min(Math.ceil(calculatedRadiusKm), maxRadiusKm);
        
        setCenter([mapCenter.lat, mapCenter.lng]);
        setRadiusKm(cappedRadiusKm);
        
        // Notify parent component of radius change
        if (onRadiusUpdate) {
            onRadiusUpdate(cappedRadiusKm);
        }
    }, [map, maxRadiusKm, onRadiusUpdate]);

    // Set initial center and radius
    useEffect(() => {
        if (!center && map) {
            updateCenterAndRadius();
        }
    }, [center, map, updateCenterAndRadius]);

    if (!showOverlay || !center) return null;

    const searchRadiusMeters = radiusKm * 1000;

    return (
        <>
            <Circle
                center={center}
                radius={searchRadiusMeters}
                pathOptions={{
                    color: '#2563eb',
                    weight: 3,
                    opacity: 0.8,
                    fillColor: '#2563eb',
                    fillOpacity: 0.1,
                    dashArray: '10, 10'
                }}
            />
        </>
    );
};

const createEmojiIcon = (emoji, status = 'unsaved') => {
    const cacheKey = `${emoji}-${status}`;
    
    if (iconCache.has(cacheKey)) {
        return iconCache.get(cacheKey);
    }
    
    const size = 32;
    let borderColor, backgroundColor, emojiStyle;
    
    switch (status) {
        case 'saved':
            borderColor = '#10b981'; // Green for saved with name
            backgroundColor = 'rgba(255, 255, 255, 0.9)';
            emojiStyle = 'filter: none;';
            break;
        case 'seen':
            borderColor = '#9ca3af'; // Grey for seen without name
            backgroundColor = 'rgba(255, 255, 255, 0.7)';
            emojiStyle = 'filter: grayscale(80%) opacity(0.7);';
            break;
        case 'labeled':
            borderColor = '#f59e0b'; // Orange for places with existing labels
            backgroundColor = 'rgba(255, 255, 255, 0.9)';
            emojiStyle = 'filter: none;';
            break;
        default: // 'unsaved'
            borderColor = '#ef4444'; // Red for unsaved
            backgroundColor = 'rgba(255, 255, 255, 0.9)';
            emojiStyle = 'filter: none;';
    }
    
    const icon = new L.DivIcon({
        html: `
            <div style="
                display: flex;
                align-items: center;
                justify-content: center;
                width: ${size}px;
                height: ${size}px;
                background: ${backgroundColor};
                border: 3px solid ${borderColor};
                border-radius: 50%;
                font-size: 16px;
                box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
                cursor: pointer;
                transition: all 0.2s ease;
                backdrop-filter: blur(4px);
                ${emojiStyle}
            " onmouseover="this.style.transform='scale(1.1)'; this.style.boxShadow='0 4px 12px rgba(0, 0, 0, 0.3)';" onmouseout="this.style.transform='scale(1)'; this.style.boxShadow='0 2px 8px rgba(0, 0, 0, 0.2)';">
                <span style="${emojiStyle}">${emoji}</span>
            </div>
        `,
        className: 'emoji-marker',
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2],
    });
    
    iconCache.set(cacheKey, icon);
    return icon;
};

const InteractiveMap = ({
    mapRef,
    initialMapCoordinates,
    places,
    savedPlaces,
    onMarkerClick,
    onLocationSearch
}) => {
    const [showSearchAreaOverlay, setShowSearchAreaOverlay] = useState(true);
    const [showInfoPanel, setShowInfoPanel] = useState('none'); // 'none', 'search', 'legend'
    const [currentRadiusKm, setCurrentRadiusKm] = useState(25);
    // Memoize saved places with their status for better performance
    const savedPlacesMap = useMemo(() => {
        const map = new Map();
        Array.from(savedPlaces).forEach(savedPlace => {
            try {
                const parsedPlace = JSON.parse(savedPlace);
                const wikidataID = parsedPlace.place.wikidataID;
                // Determine status: 'saved' if has label, 'seen' if marked as seen
                const status = parsedPlace.isSeen ? 'seen' : 'saved';
                map.set(wikidataID, status);
            } catch (e) {
                // Ignore invalid JSON
            }
        });
        return map;
    }, [savedPlaces]);

    // Memoize markers to prevent unnecessary re-renders
    const memoizedMarkers = useMemo(() => {
        return places.map((place, index) => {
            let status = savedPlacesMap.get(place.wikidataID) || 'unsaved';
            
            // If place has existing label but isn't saved, mark as 'labeled'
            if (status === 'unsaved' && place.hasExistingLabel()) {
                status = 'labeled';
            }
            
            const placeEmoji = place.getTypeIcon();
            
            return (
                <Marker
                    key={`${place.wikidataID}-${index}`}
                    position={[place.lat, place.lon]}
                    icon={createEmojiIcon(placeEmoji, status)}
                    eventHandlers={{
                        click: () => onMarkerClick(place),
                    }}
                >
                    <Tooltip
                        direction="top"
                        offset={[0, -20]}
                        opacity={0.95}
                        className="map-tooltip"
                    >
                        <div style={{
                            padding: '6px 10px',
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            color: '#1e293b',
                            textAlign: 'center',
                            minWidth: '120px'
                        }}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '4px',
                                marginBottom: '4px'
                            }}>
                                <span style={{ fontSize: '1rem' }}>{placeEmoji}</span>
                                <span style={{ 
                                    color: place.isUnnamedPlace() ? '#ef4444' : '#1e293b',
                                    fontStyle: place.isUnnamedPlace() ? 'italic' : 'normal'
                                }}>
                                    {place.getDisplayName()}
                                </span>
                                {place.isUnnamedPlace() && (
                                    <span style={{ 
                                        fontSize: '0.8rem',
                                        backgroundColor: '#fef2f2',
                                        color: '#dc2626',
                                        padding: '2px 6px',
                                        borderRadius: '4px',
                                        fontWeight: 500
                                    }}>
                                        ⚠️
                                    </span>
                                )}
                            </div>
                            {place.hasExistingLabel() && (
                                <div style={{
                                    fontSize: '0.75rem',
                                    color: '#f59e0b',
                                    marginBottom: '4px',
                                    fontStyle: 'italic',
                                    textAlign: 'center',
                                    backgroundColor: '#fffbeb',
                                    padding: '3px 6px',
                                    borderRadius: '4px',
                                    border: '1px solid #fed7aa'
                                }}>
                                    🏷️ Current: "{place.existingLabel}"
                                </div>
                            )}
                            <div style={{
                                fontSize: '0.75rem',
                                color: '#64748b',
                                marginBottom: '2px'
                            }}>
                                {place.getTypeDisplayName()}
                            </div>
                            {place.isUnnamedPlace() && (
                                <div style={{
                                    fontSize: '0.7rem',
                                    color: '#dc2626',
                                    marginBottom: '4px',
                                    fontStyle: 'italic',
                                    textAlign: 'center',
                                    backgroundColor: '#fef2f2',
                                    padding: '3px 6px',
                                    borderRadius: '4px',
                                    border: '1px solid #fecaca'
                                }}>
                                    ℹ️ This place has no name in Wikidata yet
                                </div>
                            )}
                            <div style={{
                                fontSize: '0.75rem',
                                color: status === 'saved' ? '#10b981' : status === 'seen' ? '#9ca3af' : status === 'labeled' ? '#f59e0b' : '#ef4444',
                                fontWeight: 500
                            }}>
                                {status === 'saved' ? '✓ Endonym added' : 
                                 status === 'seen' ? '👁 Marked as seen' : 
                                 status === 'labeled' ? '🏷️ Has existing label' : 
                                 'Click to add endonym'}
                            </div>
                        </div>
                    </Tooltip>
                </Marker>
            );
        });
    }, [places, savedPlacesMap, onMarkerClick]);
    return (
        <div style={{ position: 'relative', height: '100%', width: '100%' }}>
            <MapContainer
                center={initialMapCoordinates}
                zoom={10}
                style={{height: '100%', width: '100%'}}
                ref={mapRef}>
                <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                />
                
                {/* Clustered Markers for better performance */}
                <MarkerClusterGroup
                    chunkedLoading
                    maxClusterRadius={50}
                    disableClusteringAtZoom={15}
                    spiderfyOnMaxZoom={true}
                    showCoverageOnHover={false}
                    zoomToBoundsOnClick={true}
                    iconCreateFunction={(cluster) => {
                        const count = cluster.getChildCount();
                        const size = count < 10 ? 'small' : count < 100 ? 'medium' : 'large';
                        const sizeMap = { small: 30, medium: 40, large: 50 };
                        const iconSize = sizeMap[size];
                        
                        return new L.DivIcon({
                            html: `
                                <div style="
                                    display: flex;
                                    align-items: center;
                                    justify-content: center;
                                    width: ${iconSize}px;
                                    height: ${iconSize}px;
                                    background: linear-gradient(135deg, #2563eb 0%, #3b82f6 100%);
                                    border: 2px solid #ffffff;
                                    border-radius: 50%;
                                    color: white;
                                    font-weight: 600;
                                    font-size: ${iconSize < 35 ? '12px' : '14px'};
                                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
                                    cursor: pointer;
                                ">
                                    ${count}
                                </div>
                            `,
                            className: 'marker-cluster',
                            iconSize: [iconSize, iconSize],
                            iconAnchor: [iconSize / 2, iconSize / 2],
                        });
                    }}
                >
                    {memoizedMarkers}
                </MarkerClusterGroup>
                
                {/* Search Area Overlay */}
                <SearchAreaOverlay 
                    showOverlay={showSearchAreaOverlay}
                    onToggleOverlay={setShowSearchAreaOverlay}
                    onRadiusUpdate={setCurrentRadiusKm}
                />
            </MapContainer>
            
            {/* MapSearchControl moved to ControlPanel */}
            
            {/* Info Panel Toggle Button */}
            <Box
                sx={{
                    position: 'absolute',
                    bottom: 8,
                    right: 8,
                    zIndex: 999,
                    display: 'flex',
                    gap: 1
                }}
            >
                <IconButton
                    onClick={() => setShowInfoPanel(showInfoPanel === 'legend' ? 'none' : 'legend')}
                    sx={{
                        backgroundColor: showInfoPanel === 'legend' ? 'primary.main' : 'rgba(255, 255, 255, 0.95)',
                        color: showInfoPanel === 'legend' ? 'white' : 'primary.main',
                        backdropFilter: 'blur(12px)',
                        border: '1px solid rgba(226, 232, 240, 0.6)',
                        '&:hover': {
                            backgroundColor: showInfoPanel === 'legend' ? 'primary.dark' : 'rgba(255, 255, 255, 0.98)',
                        }
                    }}
                >
                    <InfoIcon />
                </IconButton>
                <IconButton
                    onClick={() => {
                        setShowSearchAreaOverlay(!showSearchAreaOverlay);
                        setShowInfoPanel(showInfoPanel === 'search' ? 'none' : 'search');
                    }}
                    sx={{
                        backgroundColor: showSearchAreaOverlay ? 'primary.main' : 'rgba(255, 255, 255, 0.95)',
                        color: showSearchAreaOverlay ? 'white' : 'primary.main',
                        backdropFilter: 'blur(12px)',
                        border: '1px solid rgba(226, 232, 240, 0.6)',
                        '&:hover': {
                            backgroundColor: showSearchAreaOverlay ? 'primary.dark' : 'rgba(255, 255, 255, 0.98)',
                        }
                    }}
                >
                    <SearchAreaIcon />
                </IconButton>
            </Box>

            {/* Legend Panel */}
            {showInfoPanel === 'legend' && (
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: 60,
                        right: 8,
                        zIndex: 998,
                        maxWidth: '260px'
                    }}
                >
                    <Paper
                        elevation={3}
                        sx={{
                            p: 2,
                            backgroundColor: 'rgba(255, 255, 255, 0.95)',
                            backdropFilter: 'blur(12px)',
                            borderRadius: 3,
                            border: '1px solid rgba(226, 232, 240, 0.6)'
                        }}
                    >
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1.5, color: '#1e293b' }}>
                            Map Legend
                        </Typography>
                        
                        {/* Status Colors */}
                        <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', mb: 1, display: 'block' }}>
                            Place Status:
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 1.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ 
                                    width: 16, 
                                    height: 16, 
                                    borderRadius: '50%', 
                                    border: '2px solid #ef4444',
                                    backgroundColor: 'white'
                                }} />
                                <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                                    No name yet
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ 
                                    width: 16, 
                                    height: 16, 
                                    borderRadius: '50%', 
                                    border: '2px solid #f59e0b',
                                    backgroundColor: 'white'
                                }} />
                                <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                                    Has existing label
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ 
                                    width: 16, 
                                    height: 16, 
                                    borderRadius: '50%', 
                                    border: '2px solid #10b981',
                                    backgroundColor: 'white'
                                }} />
                                <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                                    Endonym added
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ 
                                    width: 16, 
                                    height: 16, 
                                    borderRadius: '50%', 
                                    border: '2px solid #9ca3af',
                                    backgroundColor: 'rgba(255, 255, 255, 0.7)',
                                    filter: 'grayscale(80%)'
                                }} />
                                <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                                    Marked as seen
                                </Typography>
                            </Box>
                        </Box>
                        
                        {/* Icons */}
                        <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', mb: 1, display: 'block' }}>
                            Icons show place types:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            <Typography variant="caption" sx={{ fontSize: '0.7rem', backgroundColor: '#f8fafc', padding: '2px 4px', borderRadius: 1 }}>
                                🏙️ Cities
                            </Typography>
                            <Typography variant="caption" sx={{ fontSize: '0.7rem', backgroundColor: '#f8fafc', padding: '2px 4px', borderRadius: 1 }}>
                                🏘️ Towns
                            </Typography>
                            <Typography variant="caption" sx={{ fontSize: '0.7rem', backgroundColor: '#f8fafc', padding: '2px 4px', borderRadius: 1 }}>
                                🏞️ Nature
                            </Typography>
                            <Typography variant="caption" sx={{ fontSize: '0.7rem', backgroundColor: '#f8fafc', padding: '2px 4px', borderRadius: 1 }}>
                                ⛰️ Mountains
                            </Typography>
                            <Typography variant="caption" sx={{ fontSize: '0.7rem', backgroundColor: '#f8fafc', padding: '2px 4px', borderRadius: 1 }}>
                                🌊 Water
                            </Typography>
                        </Box>
                    </Paper>
                </Box>
            )}

            {/* Search Area Information Panel */}
            {showSearchAreaOverlay && (
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: 60,
                        right: 8,
                        zIndex: 997, // Lower than legend panel
                        maxWidth: '280px'
                    }}
                >
                    <Fade in={showSearchAreaOverlay}>
                    <Paper
                        elevation={6}
                        sx={{
                            p: 2.5,
                            backgroundColor: 'rgba(37, 99, 235, 0.95)',
                            backdropFilter: 'blur(12px)',
                            borderRadius: 3,
                            border: '2px solid #2563eb',
                            color: 'white'
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <SearchAreaIcon sx={{ color: 'white', fontSize: '1.2rem' }} />
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    Search Area ({currentRadiusKm}km radius)
                                </Typography>
                            </Box>
                            <IconButton
                                size="small"
                                onClick={() => setShowSearchAreaOverlay(false)}
                                sx={{ 
                                    color: 'white', 
                                    p: 0.5,
                                    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }
                                }}
                            >
                                <CloseIcon sx={{ fontSize: '1rem' }} />
                            </IconButton>
                        </Box>
                        <Typography variant="caption" sx={{ 
                            display: 'block', 
                            lineHeight: 1.4,
                            opacity: 0.95
                        }}>
                            The blue dashed circle shows the current {currentRadiusKm}km search area around the map center. 
                            Zoom in/out to adjust the radius (max 25km).
                        </Typography>
                    </Paper>
                    </Fade>
                </Box>
            )}
        </div>
    );
};

export default InteractiveMap;

// Note: The MapSearchControl is positioned absolutely over the map
// It uses Nominatim (OpenStreetMap) geocoding service which is free and doesn't require an API key
// Results include cities, towns, addresses, and landmarks worldwide